import {
  Component,
  Input,
  ContentChildren,
  QueryList,
  TemplateRef,
  AfterContentInit,
  EventEmitter,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort, MatSortHeader } from '@angular/material/sort';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { PaginationOptions } from '@uw/core/models/filter.model';
import { ActionableEntity, IsLoadedTableDatasourceMetadata } from '@uw/core/models/is-loaded-list-metadata.model';
import { DefaultPaginationOptions } from '@uw/core/utils/pagination-util';
import * as PaginationUtil from '@uw/core/utils/pagination-util';
import { sortList } from '@uw/core/utils/sort-util';
import { ColumnSizeTypes } from './uw-table.model';
import * as UwTableUtil from '@uw/shared/components/uw-table/uw-table-util';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NoRecordContainerComponent } from '../no-record-container/no-record-container.component';
import { TranslateModule } from '@ngx-translate/core';

export interface TableColumn {
  columnDef: string;
  header: string;
  columnSize?: ColumnSizeTypes
}

export interface DefaultSortParams {
  sortColumn: string;
  sortDirection: 'asc' | 'desc'
}

@Component({
    selector: 'uw-dynamic-table',
    templateUrl: './dynamic-table.component.html',
    styleUrls: ['./dynamic-table.component.scss'],
    imports: [MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, NgClass, MatCellDef, MatCell, NgTemplateOutlet, MatIconButton, MatMenuTrigger, MatIcon, MatMenu, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatPaginator, NoRecordContainerComponent, TranslateModule]
})
export class DynamicTableComponent<T extends ActionableEntity> implements AfterContentInit {
  @Input() columns: TableColumn[] = [];

  selectedRow: any = null; // Selected row reference

  @Input()
  metadata!: IsLoadedTableDatasourceMetadata<T>;

  @Input()
  paginationOptions: PaginationOptions | null = DefaultPaginationOptions;  

  @Input()
  defaultSortParams!: Sort;

  @Output()
  pageChange = new EventEmitter<PageEvent>();

  @Output()
  sortChange = new EventEmitter<any>();

  @ContentChildren(TemplateRef) templates!: QueryList<TemplateRef<any>>;

  @ViewChild(MatSort, { static: false })
  sort!: MatSort;

  nonActionColumns!: TableColumn[];

  actionsColumn!: TableColumn | undefined;  

  length!: number;
  pageSize!: number;
  pageIndex!: number;

  // Explicitly define columnTemplates as an indexable object
  columnTemplates: { [key: string]: TemplateRef<any> } = {};

  ngAfterContentInit() {
    this.templates.forEach((template, index) => {
      const column = this.columns[index];
      if (column) {
        this.columnTemplates[column.columnDef] = template;
      }
    });
  }

  ngAfterViewInit() {
    if (this.metadata && this.sort) {
      this.metadata.dataSource.sort = this.sort;
    }

    if (this.metadata && this.defaultSortParams) {
      this.handleSortChange(this.defaultSortParams);
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metadata?.currentValue) {
      if (!this.metadata.dataSource) {
        this.metadata.dataSource = new MatTableDataSource<T>();
      }
      this.metadata.dataSource.sort = this.sort;
      
      let selectedRowIdentifier = this.metadata.selectedRowIdentifier!;
      if (selectedRowIdentifier && selectedRowIdentifier.property) {
        this.selectedRow = this.metadata.entityList.find(row => row[selectedRowIdentifier.property] == selectedRowIdentifier?.value );
      }
      
    }

    if (changes.columns?.currentValue) {
      this.nonActionColumns = this.columns.filter(c => c.columnDef != 'actions');
      this.actionsColumn = this.columns.find(c => c.columnDef == 'actions');
    }

    if (
      changes &&
      changes.paginationOptions &&
      changes.paginationOptions.currentValue
    ) {
      this.pageIndex =
        this.paginationOptions?.page !== undefined &&
        this.paginationOptions.page > 0
          ? this.paginationOptions.page
          : 0;
      this.pageSize = this.paginationOptions?.limit
        ? this.paginationOptions.limit
        : 10;

      if (this.paginationOptions?.length) {
        this.length = this.paginationOptions.length;
      }
    }

    if (this.metadata && this.defaultSortParams) {
      this.handleSortChange(this.defaultSortParams);
    }

  }

  get displayedColumns(): string[] {
    return this.columns.map((c) => c.columnDef);
  }

  handleSortChange(event: Sort) {
    const direction = event.direction || undefined;
    this.metadata.entityList = sortList(this.metadata.entityList!, event.active  as keyof typeof this.metadata.entityList[0], direction);
    this.refreshPage();  
  }

  refreshPage(pageDetails?: PageEvent) {
    if (!pageDetails) {
      pageDetails = {
        pageSize: this.pageSize,
        length: this.length,
        pageIndex: this.pageIndex,
      };
    }

    const pagedMetadata = PaginationUtil.handleClientSidePageChangeEvent(
      pageDetails,
      this.metadata.entityList
    );

    this.metadata.dataSource = new MatTableDataSource(pagedMetadata.pagedList);
    
    this.length = this.metadata.entityList.length;
    this.pageIndex = pagedMetadata.pageIndex;
    this.pageSize = pagedMetadata.pageSize;
  }

  handlePageEvent(event: PageEvent) {
    this.refreshPage(event);       
  }

  getColumnClass(size: ColumnSizeTypes) {
      return UwTableUtil.getColumnClass(this.displayedColumns.length, size);
    }
}
